import { AlertColor, Box, CircularProgress } from "@mui/material";
import React, { ReactNode } from "react";
import type { SvgIconComponent } from "@mui/icons-material";
import { ediphiPalette } from "../../styles/colors";
import { Card } from "./Card.styled";
import { HeaderText } from "../text/HeaderText.styled";
import CloseIcon from "@mui/icons-material/Close";
import TuneIcon from "@mui/icons-material/Tune";
import RefreshIcon from "@mui/icons-material/Refresh";
import { BodyText } from "../text/BodyText.styled";
import { IconButton } from "../buttons/IconButton.styled";
import PaneTooltip from "../text/PaneTooltip";
import { useDialog } from "../../context/DialogContext";
import { DialogDescription } from "../dialog/Dialog";
import { deleteWorkbookItemDescription, deleteWorkbookItemTitle } from "../../constants";

/** One button in the card header. */
interface WorkbookItemCardButton {
  /** Receives the click so the caller can anchor a floating menu to the button. */
  onClick: React.MouseEventHandler<HTMLElement>;
  /** Defaults per slot: refresh, options, delete. */
  icon?: SvgIconComponent;
  /** A bare glyph is not guessable. Defaults to a phrase built from `itemNoun`. */
  tooltip?: string;
  /** Omits the button rather than greying it: nothing to do, so nothing to show. Defaults to true. */
  isVisible?: boolean;
}

interface WorkbookItemCardProps {
  header: {
    text: string | ReactNode;
    subtext: string;
    /** Renders the subtext as a state rather than a subtitle: italic and a shade lighter. */
    subtextIsStatus?: boolean;
    /** A component for a plain icon, or a node when the caller renders its own (chart type icons). */
    icon?: SvgIconComponent | ReactNode;
    /** Plain-text name for the delete dialog copy. Required when `text` is a ReactNode. */
    name?: string;
  };
  /** What this card holds — "report", "chart". Builds the button tooltips and the dialog copy. */
  itemNoun: string;
  /** The leading action. A report refreshes itself; a chart jumps to itself — hence the overrides. */
  action: WorkbookItemCardButton;
  edit: WorkbookItemCardButton;
  remove: Omit<WorkbookItemCardButton, "onClick"> & {
    /** Runs once the deletion is confirmed, so there is no click event to receive. */
    onClick: () => void | Promise<void>;
    /** Info shown in the confirm dialog. Required: only the caller knows what its deletion touches. */
    notice: string;
    /** Severity of `notice`. Defaults to info; use "warning" when the deletion breaks something. */
    noticeSeverity?: AlertColor;
  };
  loading?: boolean;
  /** Replaces the subtext while loading — say what is happening, e.g. "Refreshing data…". */
  loadingText?: string;
}

const WorkbookItemCard = ({ header, itemNoun, action, edit, remove, loading, loadingText }: WorkbookItemCardProps) => {
  // MUI icons are React.memo objects rather than functions, so a typeof check misreads them as
  // nodes and React throws on the raw memo object. isValidElement is what separates an
  // already-built element from a component type.
  const restingIcon = React.isValidElement(header.icon)
    ? header.icon
    : header.icon
      ? React.createElement(header.icon as SvgIconComponent, { sx: { fontSize: "18px" } })
      : null;
  // The spinner takes the icon's place rather than overlaying the card, so progress reads on the
  // same line as the text describing it.
  const headerIcon = loading ? (
    <CircularProgress size={18} sx={{ color: ediphiPalette.info600, flexShrink: 0 }} />
  ) : (
    restingIcon
  );
  const { showDialog, hideDialog } = useDialog();
  // `itemNoun` is lowercase for mid-sentence dialog copy; the options tooltip opens with it.
  const capitalizedItemNoun = `${itemNoun.charAt(0).toUpperCase()}${itemNoun.slice(1)}`;

  const confirmRemove = () =>
    showDialog({
      title: deleteWorkbookItemTitle(String(header.name ?? header.text)),
      description: DialogDescription(deleteWorkbookItemDescription(itemNoun)),
      alert: { severity: remove.noticeSeverity, content: remove.notice },
      submit: {
        destructive: true,
        action: async () => {
          hideDialog();
          await remove.onClick();
        },
      },
      cancel: { action: hideDialog },
    });

  // Resolved with ?? rather than a spread over defaults: an explicitly undefined icon or tooltip
  // from a caller would clobber the slot default.
  const buttons = [
    {
      slot: "action",
      Icon: action.icon ?? RefreshIcon,
      tooltip: action.tooltip ?? `Refresh ${itemNoun}`,
      onClick: action.onClick,
      isVisible: action.isVisible,
    },
    {
      slot: "edit",
      Icon: edit.icon ?? TuneIcon,
      tooltip: edit.tooltip ?? `${capitalizedItemNoun} options`,
      onClick: edit.onClick,
      isVisible: edit.isVisible,
    },
    {
      slot: "remove",
      Icon: remove.icon ?? CloseIcon,
      tooltip: remove.tooltip ?? `Delete ${itemNoun}`,
      onClick: confirmRemove,
      isVisible: remove.isVisible,
    },
  ];

  return (
    <Box sx={{ position: "relative" }}>
      {/* <Card sx={{ flexDirection: "column", gap: 1.5, border: "1px solid #CDCDCD" }}> */}
      <Card
        sx={{
          flexDirection: "column",
          gap: 1.5,
          // Not dimmed while loading. `opacity` composites the whole subtree, so it would fade the
          // spinner and the progress text — the two things worth reading just then. The card is
          // already inert: every button takes `disabled={loading}`, and nothing else here is
          // clickable.
          pointerEvents: loading ? "none" : "auto",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            {headerIcon}
            <Box sx={{ minWidth: 0 }}>
              <HeaderText
                title={typeof header.text === "string" ? header.text : undefined}
                sx={{
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  overflow: "hidden",
                  // Without this a name typed as one unbroken string has no break opportunity, so the
                  // clamp has nothing to clamp and the line overflows anyway.
                  wordBreak: "break-word",
                }}
              >
                {header.text}
              </HeaderText>
              <BodyText
                noWrap
                sx={
                  header.subtextIsStatus
                    ? // noWrap clips at the content box, and an italic glyph leans past its advance width —
                      // without the trailing pixels the last letter loses its tail.
                      { fontStyle: "italic", color: ediphiPalette.greyText, paddingRight: "2px" }
                    : undefined
                }
              >
                {loadingText ?? header.subtext}
              </BodyText>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
            {/* `loading` only disables — a button vanishing mid-refresh would shift the row under
                the cursor. */}
            {buttons
              .filter(({ isVisible }) => isVisible !== false)
              .map(({ slot, Icon, tooltip, onClick }) => (
                <PaneTooltip key={slot} title={tooltip}>
                  <IconButton onClick={onClick} disabled={loading}>
                    <Icon />
                  </IconButton>
                </PaneTooltip>
              ))}
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default WorkbookItemCard;
